import create from "zustand";
import { devtools, persist } from "zustand/middleware";
import { calcLampsPositions } from "./helpers/calcLampsPositions";
import { DecorItemI, OriginDecorI } from "../shared";
import { addDecor } from "./actions/decors/addDecor";
import { removeDecor } from "./actions/decors/removeDecor";
import { updateDecor } from "./actions/decors/updateDecor";
import { addTireRack } from "./actions/tireRacks/addTireRack";
import { removeTireRack } from "./actions/tireRacks/removeTireRack";
import { updateTireRack } from "./actions/tireRacks/updateTireRack";
import produce from "immer";
import { useStoreSystem } from "./useStoreSystem";
import { ShelvingStoreInterface } from "./useShelvingStore";
import { Vector3 } from "three";
import { SideTypeWithoutTop } from "./types/store";
import { TireRackItemI } from "./types/store";
import { ConstructionElement } from "../controllers/types";

export type RoomParams = {
    height: number;
    length: number;
    depth: number;
};

export interface storeRoomInterface {
    lamp: {
        positions: number[];
        count: number;
    };
    height: number;
    length: number;
    depth: number;
    cameraDistance: number;
    decors: DecorItemI[];
    tireRacks: TireRackItemI[];

    setCameraDistance: (distance: number) => void;
    setLength: (length: number) => void;
    setCountLamp: (length: number) => void;
    setHeight: (height: number) => void;
    setDepth: (depth: number) => void;
    addDecor: (decorItem: OriginDecorI) => void;
    removeDecor: (decorItemItem: string) => void;
    updateDecor: (decor: DecorItemI) => void;
    moveDecors: (deltaPos: [number, number, number], side: Array<SideTypeWithoutTop> | "all") => void;
    addTireRack: (construction: ConstructionElement) => void;
    removeTireRack: (id: string) => void;
    updateTireRack: (tireRack: TireRackItemI) => void;
    moveTireRacks: (deltaPos: [number, number, number]) => void;
    resetRoomStore: (isWithDecor: boolean, isWithRoom: boolean) => void;
    loadProject: (project: any) => void;
}

const INITIAL_STORE_ROOM = {
    decors: [],
    tireRacks: [],
    height: 3200,
    length: 4880,
    depth: 6000,
    lamp: {
        count: 4,
        positions: calcLampsPositions(4),
    },
};

export const useStoreRoom = create<
    storeRoomInterface,
    [["zustand/devtools", never], ["zustand/persist", storeRoomInterface]]
>(
    devtools(
        persist(
            (set, get) => ({
                height: INITIAL_STORE_ROOM.height,
                length: INITIAL_STORE_ROOM.length,
                depth: INITIAL_STORE_ROOM.depth,
                lamp: {
                    count: INITIAL_STORE_ROOM.lamp.count,
                    positions: INITIAL_STORE_ROOM.lamp.positions,
                },
                decors: [],
                tireRacks: [],
                cameraDistance: 0,
                setCameraDistance: (distance) => set({ cameraDistance: distance }),
                setCountLamp: (count) => {
                    if (count !== get().lamp.count) {
                        set({ lamp: { ...get().lamp, count: count, positions: calcLampsPositions(count) } });
                    }
                },
                setLength: (length) => {
                    set({ length: length });
                    const lampsCount = Math.round((length - 500) / 1000);
                    get().setCountLamp(lampsCount);
                },
                setHeight: (height) => {
                    set({ height: height });
                },
                setDepth: (depth) => {
                    set({ depth: depth });
                },
                addDecor: (decorItem) => set({ decors: addDecor(decorItem, useStoreSystem.getState().currentSide) }),
                removeDecor: (decorItemId) => set({ decors: removeDecor(decorItemId, get().decors) }),
                updateDecor: (decorItem) => set({ decors: updateDecor(decorItem, get().decors) }),
                addTireRack: (construction) => set({ tireRacks: addTireRack(construction) }),
                removeTireRack: (id) => set({ tireRacks: removeTireRack(id, get().tireRacks) }),
                updateTireRack: (tireRack) => set({ tireRacks: updateTireRack(tireRack, get().tireRacks) }),
                moveTireRacks: (deltaPos) => {
                    set({
                        tireRacks: get().tireRacks.map((tireRack: TireRackItemI) => {
                            const rackPos = tireRack.position;
                            const newRackPos: [number, number, number] = [
                                rackPos[0] + deltaPos[0],
                                rackPos[1] + deltaPos[1],
                                rackPos[2] + deltaPos[2],
                            ];
                            return { ...tireRack, position: newRackPos };
                        }),
                    });
                },
                resetRoomStore: (isWithDecor, isWithRoom) =>
                    set(
                        produce((state: storeRoomInterface) => {
                            if (isWithDecor) {
                                state.decors = [];
                                state.tireRacks = [];
                            }
                            if (isWithRoom) {
                                state.height = INITIAL_STORE_ROOM.height;
                                state.length = INITIAL_STORE_ROOM.length;
                                state.depth = INITIAL_STORE_ROOM.depth;
                                state.lamp = INITIAL_STORE_ROOM.lamp;
                            }
                            return state;
                        })
                    ),
                moveDecors: (deltaPos, side) => {
                    set({
                        decors: get().decors.map((decor: DecorItemI) => {
                            if (side?.includes(decor.side) || side === "all") {
                                const decorPos = decor.position;
                                const newDecorPos: [number, number, number] = [
                                    decorPos[0] + deltaPos[0],
                                    decorPos[1] + deltaPos[1],
                                    decorPos[2] + deltaPos[2],
                                ];
                                return { ...decor, position: newDecorPos };
                            }
                            return decor;
                        }),
                    });
                },
                loadProject: (project) => {
                    set(() => ({ ...INITIAL_STORE_ROOM }));
                    Object.entries(project).forEach(([stateKey, stateValue]) => {
                        set(
                            produce((state) => {
                                state[stateKey] = stateValue;
                            })
                        );
                    });
                },
            }),
            {
                version: 10927,
                name: "StoreRoom",
            }
        ),
        {
            name: "storeRoom",
            enabled: process.env.NODE_ENV === "development",
        }
    )
);
